import React, { cloneElement, isValidElement } from 'react';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import type { FullscreenOverlayTriggerProps } from './types';

/**
 * FullscreenOverlayTrigger Component
 *
 * Trigger element that opens the fullscreen overlay when clicked.
 * Supports the `asChild` pattern from Constellation v10 for flexible composition.
 *
 * @example Default usage (renders a button)
 * <FullscreenOverlay.Trigger>Open Overlay</FullscreenOverlay.Trigger>
 *
 * @example With asChild (uses existing element)
 * <FullscreenOverlay.Trigger asChild>
 *   <button className="custom-button">Open Overlay</button>
 * </FullscreenOverlay.Trigger>
 */
export const FullscreenOverlayTrigger = ({ asChild = false, children }: FullscreenOverlayTriggerProps) => {
  const { open } = useFullscreenOverlay();

  const handleClick = () => {
    open();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  if (asChild && isValidElement(children)) {
    // Clone the child element and merge our props with existing props
    return cloneElement(children as React.ReactElement<any>, {
      onClick: (event: React.MouseEvent) => {
        // Call existing onClick if it exists
        if (children.props.onClick) {
          children.props.onClick(event);
        }
        // Then call our handler
        handleClick();
      },
      onKeyDown: (event: React.KeyboardEvent) => {
        // Call existing onKeyDown if it exists
        if (children.props.onKeyDown) {
          children.props.onKeyDown(event);
        }
        // Then call our handler
        handleKeyDown(event);
      },
    });
  }

  // Default: render as button
  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
};
